import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { BarChart3, Mail, ArrowLeft, CheckCircle, Building2 } from 'lucide-react';

const Login = ({ onLogin }) => {
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '', organization_name: '' });
  const [loading, setLoading] = useState(false);
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);

  // Auto-suggest organization name from email
  useEffect(() => {
    if (registerData.email && !registerData.organization_name) {
      const parts = registerData.email.split('@');
      if (parts.length === 2) {
        const domain = parts[1].split('.')[0];
        // Only suggest if domain looks like a company (not gmail, hotmail etc.)
        const freeProviders = ['gmail', 'hotmail', 'yahoo', 'outlook', 'icloud', 'gmx', 'web', 'mail', 'proton', 'protonmail'];
        if (!freeProviders.includes(domain.toLowerCase())) {
          setRegisterData(prev => ({ 
            ...prev, 
            organization_name: domain.charAt(0).toUpperCase() + domain.slice(1) 
          }));
        }
      }
    }
  }, [registerData.email]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      toast.success('Erfolgreich angemeldet!');
      onLogin(response.data.access_token, response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, registerData);
      toast.success('Erfolgreich registriert!');
      onLogin(response.data.access_token, response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Bitte geben Sie Ihre E-Mail-Adresse ein');
      return;
    }
    
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSuccess(true);
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen des Passworts');
    } finally {
      setForgotLoading(false);
    }
  };

  const resetForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setForgotSuccess(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background bg-noise p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-md mb-4">
            <BarChart3 className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Task2Plan</h1>
          <p className="text-muted-foreground">Verwalten Sie Ihre Projekte und Kosten effizient</p>
        </div>

        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login" data-testid="login-tab">Anmelden</TabsTrigger>
            <TabsTrigger value="register" data-testid="register-tab">Registrieren</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <Card data-testid="login-card">
              <CardHeader>
                <CardTitle>Anmelden</CardTitle>
                <CardDescription>Melden Sie sich bei Ihrem Konto an</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">E-Mail</Label>
                    <Input
                      id="login-email"
                      type="email"
                      data-testid="login-email-input"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password">Passwort</Label>
                      <button
                        type="button"
                        onClick={() => { setForgotEmail(loginData.email); setShowForgotPassword(true); }}
                        className="text-sm text-primary hover:underline"
                        data-testid="forgot-password-link"
                      >
                        Passwort vergessen?
                      </button>
                    </div>
                    <Input
                      id="login-password"
                      type="password"
                      data-testid="login-password-input"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
                    {loading ? 'Laden...' : 'Anmelden'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="register">
            <Card data-testid="register-card">
              <CardHeader>
                <CardTitle>Registrieren</CardTitle>
                <CardDescription>Erstellen Sie ein neues Konto und Ihre Organisation</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Ihr Name</Label>
                    <Input
                      id="register-name"
                      type="text"
                      data-testid="register-name-input"
                      value={registerData.name}
                      onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">E-Mail</Label>
                    <Input
                      id="register-email"
                      type="email"
                      data-testid="register-email-input"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Passwort</Label>
                    <Input
                      id="register-password"
                      type="password"
                      data-testid="register-password-input"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-org" className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Organisationsname
                    </Label>
                    <Input
                      id="register-org"
                      type="text"
                      placeholder="z.B. Musterfirma AG"
                      data-testid="register-org-input"
                      value={registerData.organization_name}
                      onChange={(e) => setRegisterData({ ...registerData, organization_name: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Optional - wird aus Ihrer E-Mail-Domain abgeleitet
                    </p>
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-button">
                    {loading ? 'Laden...' : 'Konto erstellen'}
                  </Button>
                  <p className="text-xs text-center text-muted-foreground">
                    Sie werden automatisch Administrator Ihrer Organisation
                  </p>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={(open) => { if (!open) resetForgotPassword(); else setShowForgotPassword(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {!forgotSuccess && <Mail className="w-5 h-5" />}
              {forgotSuccess ? 'E-Mail gesendet' : 'Passwort zurücksetzen'}
            </DialogTitle>
            <DialogDescription>
              {forgotSuccess 
                ? 'Prüfen Sie Ihren Posteingang für das neue Passwort.'
                : 'Geben Sie Ihre E-Mail-Adresse ein, um ein neues Passwort zu erhalten.'
              }
            </DialogDescription>
          </DialogHeader>
          
          {!forgotSuccess ? (
            <form onSubmit={handleForgotPassword} className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">E-Mail-Adresse</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="ihre@email.com"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={resetForgotPassword}
                  className="flex-1"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zurück
                </Button>
                <Button 
                  type="submit" 
                  disabled={forgotLoading}
                  className="flex-1"
                  data-testid="forgot-submit-button"
                >
                  {forgotLoading ? 'Senden...' : 'Neues Passwort anfordern'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                Prüfen Sie auch Ihren Spam-Ordner.
              </p>
            </form>
          ) : (
            <div className="space-y-4 pt-2">
              <div className="flex flex-col items-center py-4">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-center text-muted-foreground">
                  Falls ein Konto mit <strong>{forgotEmail}</strong> existiert, wurde ein neues Passwort an diese Adresse gesendet.
                </p>
              </div>
              <Button 
                onClick={resetForgotPassword}
                className="w-full"
                data-testid="back-to-login-button"
              >
                Zurück zur Anmeldung
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
